import { useState } from "react";
import { useNavigate } from "react-router";
import t from "@/lang/lang";
import { Label } from "@/shared/components/ui/label";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { CopyPlus } from "lucide-react";
import {
  useQpmCatalogCopyItemsMutation,
  useQpmCatalogCreateMutation,
} from "@/services/backend/qpm/catalog/api/catalog-api";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { CustomerSelect } from "@/services/backend/customers/components/customer-select";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import { CatalogSourceSelect } from "@/routes/base-data/processes/qpm/catalog/_components/catalog-source-select";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";

const CATALOG_OVERVIEW_PATH = "/base-data/processes/qpm/catalog";

export function CatalogCopyActions({ catalogId }: { catalogId: string }) {
  return (
    <div className="flex items-center gap-2">
      <CopyIntoExistingCatalogDialog targetCatalogId={catalogId} />
      <CreateCatalogFromSourceDialog sourceCatalogDefaultId={catalogId} />
    </div>
  );
}

function CopyIntoExistingCatalogDialog({
  targetCatalogId,
}: {
  targetCatalogId: string;
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [sourceCatalogId, setSourceCatalogId] = useState("");
  const [copyItems, copyState] = useQpmCatalogCopyItemsMutation();

  const onCopy = async () => {
    if (!sourceCatalogId) {
      return;
    }

    try {
      await copyItems({
        sourceCatalogId,
        targetCatalogId,
      }).unwrap();

      toast({
        title: t("Katalog kopiert"),
        description: t("Die Katalogdaten wurden übernommen."),
      });
      setOpen(false);
      setSourceCatalogId("");
      copyState.reset();
    } catch (err) {
      const parsedError = parseRTKQueryError(
        err as Parameters<typeof parseRTKQueryError>[0],
      );
      if (parsedError) {
        toast({
          title: parsedError.title,
          description: parsedError.description,
        });
      }
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setSourceCatalogId("");
          copyState.reset();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <CopyPlus className="mr-2 h-4 w-4" />
          {t("Von Katalog kopieren")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-screen-sm">
        <DialogHeader>
          <DialogTitle>{t("In diesen Katalog kopieren")}</DialogTitle>
          <DialogDescription>
            {t("Wählen Sie einen Quellkatalog aus.")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label>{t("Quellkatalog")}</Label>
          <CatalogSourceSelect
            value={sourceCatalogId}
            onChange={setSourceCatalogId}
            excludeCatalogId={targetCatalogId}
          />
        </div>
        <DialogFooter>
          <Button
            variant="default"
            onClick={onCopy}
            disabled={!sourceCatalogId || copyState.isLoading}
          >
            {t("Kopieren")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateCatalogFromSourceDialog({
  sourceCatalogDefaultId,
}: {
  sourceCatalogDefaultId: string;
}) {
  const { companyId } = useActiveCompany();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [sourceCatalogId, setSourceCatalogId] = useState(
    sourceCatalogDefaultId,
  );
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [customerId, setCustomerId] = useState<string>(NullUUID);
  const [createCatalog, createState] = useQpmCatalogCreateMutation();
  const [copyItems, copyState] = useQpmCatalogCopyItemsMutation();

  const onCreateFromSource = async () => {
    if (!sourceCatalogId || !name.trim()) {
      return;
    }

    try {
      const createdCatalog = await createCatalog({
        companyId,
        name: name.trim(),
        description,
        customerId: customerId !== NullUUID ? customerId : undefined,
      }).unwrap();

      await copyItems({
        sourceCatalogId,
        targetCatalogId: createdCatalog.id,
      }).unwrap();

      toast({
        title: t("Katalog erstellt"),
        description: t("Der neue Katalog wurde erstellt und befüllt."),
      });

      setOpen(false);
      setSourceCatalogId("");
      setName("");
      setDescription("");
      setCustomerId(NullUUID);
      createState.reset();
      copyState.reset();
      navigate(`${CATALOG_OVERVIEW_PATH}/${createdCatalog.id}`);
    } catch (err) {
      const parsedError = parseRTKQueryError(
        err as Parameters<typeof parseRTKQueryError>[0],
      );
      if (parsedError) {
        toast({
          title: parsedError.title,
          description: parsedError.description,
        });
      }
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          setSourceCatalogId(sourceCatalogDefaultId);
        } else {
          setSourceCatalogId(sourceCatalogDefaultId);
          setName("");
          setDescription("");
          setCustomerId(NullUUID);
          createState.reset();
          copyState.reset();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <CopyPlus className="mr-2 h-4 w-4" />
          {t("Neuen Katalog aus Vorlage")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-screen-md">
        <DialogHeader>
          <DialogTitle>{t("Neuen Katalog aus Vorlage erstellen")}</DialogTitle>
          <DialogDescription>
            {t("Der neue Katalog erhält eine eigene Metadatenbasis.")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="catalog-template-source">{t("Quellkatalog")}</Label>
            <CatalogSourceSelect
              value={sourceCatalogId}
              onChange={setSourceCatalogId}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="catalog-template-name">{t("Name")}</Label>
            <Input
              id="catalog-template-name"
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="catalog-template-description">
              {t("Beschreibung")}
            </Label>
            <Textarea
              id="catalog-template-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="catalog-template-customer">
              {t("Kunde (optional)")}
            </Label>
            <CustomerSelect
              value={customerId}
              showUnsetOption
              onValueChange={(value) => setCustomerId(value || NullUUID)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="default"
            onClick={onCreateFromSource}
            disabled={
              !sourceCatalogId ||
              !name.trim() ||
              createState.isLoading ||
              copyState.isLoading
            }
          >
            {t("Erstellen und kopieren")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
